import { Link, useNavigate } from "react-router-dom";
import { Bot, Database, Layers, Plus, Rocket, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { appTemplates } from "@/lib/builder/templates";

const quickActions = [
  {
    title: "创建应用",
    description: "通过 AI 对话或拖拽快速生成零代码应用",
    icon: Plus,
    to: "/builder",
    primary: true,
  },
  {
    title: "AI 副驾驶",
    description: "自然语言生成页面、数据模型与业务逻辑",
    icon: Bot,
    to: "/copilot",
  },
  {
    title: "数据管理",
    description: "管理业务数据表、视图与权限",
    icon: Database,
    to: "/data",
  },
  {
    title: "发布中心",
    description: "预览、发布与导出可二次开发的 React 代码",
    icon: Rocket,
    to: "/builder",
  },
];

export function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-500 to-violet-600 p-8 text-white shadow-lg">
        <div className="relative z-10">
          <h1 className="text-3xl font-bold tracking-tight">欢迎使用罢歌亭</h1>
          <p className="mt-2 max-w-xl text-indigo-100">
            AI 原生零代码应用平台。用自然语言描述需求，快速生成完整 Web 应用。
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild className="bg-white text-indigo-600 hover:bg-indigo-50">
              <Link to="/builder">
                <Sparkles className="mr-2 h-4 w-4" />
                从 AI 对话开始
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <Link to="/builder">从模板创建</Link>
            </Button>
          </div>
        </div>
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-10 right-20 h-32 w-32 rounded-full bg-cyan-300/20 blur-2xl" />
      </div>

      {/* Quick actions */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <Card
              key={action.title}
              className={`transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${action.primary ? "border-indigo-500/50" : ""}`}
            >
              <CardHeader className="pb-3">
                <Icon className={`h-6 w-6 ${action.primary ? "text-indigo-600" : "text-muted-foreground"}`} />
                <CardTitle className="text-base">{action.title}</CardTitle>
                <CardDescription className="text-xs">{action.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant={action.primary ? "default" : "outline"} size="sm" className="w-full">
                  <Link to={action.to}>进入</Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Template marketplace */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
              <Layers className="h-5 w-5 text-indigo-600" />
              模板市场
            </h2>
            <p className="text-sm text-muted-foreground">基于模板快速创建，再与 AI 对话微调。</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/builder">查看全部</Link>
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {appTemplates.map((template) => {
            const Icon = template.icon;
            return (
              <Card
                key={template.id}
                className="group cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:border-indigo-500/30 hover:shadow-md"
                onClick={() => navigate(`/builder?template=${template.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/10 transition-colors group-hover:bg-indigo-500/20">
                      <Icon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {template.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-base">{template.name}</CardTitle>
                  <CardDescription className="text-xs line-clamp-2">{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button size="sm" className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 text-white opacity-90 transition-opacity group-hover:opacity-100">
                    使用模板
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent projects */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Rocket className="h-5 w-5" />
            最近项目
          </CardTitle>
          <CardDescription>你最近编辑或发布的应用将显示在这里。</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">暂无项目，点击上方「创建应用」或选择一个模板开始。</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
